import React from 'react';
import { Settings, X } from 'lucide-react';
import { TaskyButton, TaskyCheckbox, TaskyIconButton } from '../../../components/tasky';

export type BannerStyleMode = 'none' | 'auto' | 'manual';

/**
 * 「スタイルを選ぶ」ボタンで見本を開き、選んだスタイル名を横に出す（1ヶ月前の仕様に戻した形、Discord 要望 09-29）。
 * 未選択（見本で「スタイルなしで続行」・× で解除も含む）は、商品モードなら商品に合うスタイルの自動選択、
 * フリーテキストなら登録済みスタイルからのランダム（none）になる。
 * 自動選択は以前の「自動でスタイル適応」と同じくチェックボックスでも明示する。未選択＝自動なので、
 * チェックを外すと見本を開き（外すだけの「スタイルなし」は作らない）、入れると選んだスタイルを解除する。
 */
export default function BannerStyleChoice({ mode, onChange, names, onSelect, productMode = true }: {
  mode: BannerStyleMode;
  onChange: (mode: BannerStyleMode) => void;
  names: string[];
  onSelect: () => void;
  productMode?: boolean;
}) {
  const selected = mode === 'manual' && names.length > 0;
  const fallback = productMode ? '未選択なら商品に合わせて自動選択' : '未選択なら登録済みスタイルから自動で選択';
  const autoLabel = productMode ? '商品に合わせて自動選択' : '自動で選ぶ';

  return (
    <div className="min-w-0">
      <p className="mb-2 text-sm font-semibold text-gray-700">
        スタイルを選ぶ
        <span className="ml-2 text-xs font-normal text-gray-400">（任意・{fallback}）</span>
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <TaskyButton type="button" variant="outline" onClick={onSelect}>
          <Settings />
          スタイルを選ぶ
        </TaskyButton>
        <TaskyCheckbox
          checked={!selected}
          onChange={() => (selected ? onChange('auto') : onSelect())}
          label={autoLabel}
          wrapperClassName="rounded-md border border-gray-200 px-3 py-1.5 hover:bg-gray-50"
        />
        {selected && (
          <div className="flex max-w-full items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5">
            <span className="truncate text-sm font-medium text-blue-700">
              {names.length === 1 ? names[0] : `${names.length}件選択: ${names.join('、')}`}
            </span>
            <TaskyIconButton type="button" variant="ghost" size="sm" onClick={() => onChange('auto')} aria-label="選んだスタイルを解除" className="h-6 w-6">
              <X />
            </TaskyIconButton>
          </div>
        )}
      </div>
      <p className="mt-2 text-xs leading-relaxed text-gray-500">スタイルはデザインの方向性です。見本と同じ画像になるわけではありません。</p>
    </div>
  );
}
